/**
 * Feature Flags Zustand Store
 *
 * Manages all state for the Feature Flags section including:
 * - Feature flag list with filters and pagination
 * - CRUD operations for feature flags
 * - Toggle functionality with optimistic updates
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  FeatureFlag,
  FeatureFlagScope,
  PaginationMeta,
  FeatureFlagFilters,
  CreateFeatureFlagDto,
  UpdateFeatureFlagDto,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface FeatureFlagsError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface FeatureFlagsState {
  // List
  flags: FeatureFlag[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: FeatureFlagsError | null;

  // Filters
  filters: FeatureFlagFilters;

  // Action States
  isCreating: boolean;
  isUpdating: boolean;
  isToggling: string | null; // flag id being toggled
  isDeleting: boolean;
  actionError: FeatureFlagsError | null;

  // Actions
  loadFlags: (page?: number) => Promise<void>;
  refreshFlags: () => Promise<void>;
  setFilter: <K extends keyof FeatureFlagFilters>(key: K, value: FeatureFlagFilters[K]) => void;
  clearFilters: () => void;
  createFlag: (dto: CreateFeatureFlagDto) => Promise<string | null>;
  updateFlag: (id: string, dto: UpdateFeatureFlagDto) => Promise<boolean>;
  toggleFlag: (id: string, enabled: boolean) => Promise<boolean>;
  deleteFlag: (id: string) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // List
  flags: [] as FeatureFlag[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as FeatureFlagsError | null,

  // Filters
  filters: {} as FeatureFlagFilters,

  // Action States
  isCreating: false,
  isUpdating: false,
  isToggling: null as string | null,
  isDeleting: false,
  actionError: null as FeatureFlagsError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useFeatureFlagsStore = create<FeatureFlagsState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadFlags: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      if (filters.search) params.set('search', filters.search);
      if (filters.scope) params.set('scope', filters.scope);
      if (filters.enabled !== undefined) params.set('enabled', String(filters.enabled));

      // The API client unwraps the `{ success, data }` envelope and returns `data`,
      // so `response` is `{ data: FeatureFlag[]; pagination }` directly.
      const response = await api.get<{
        data: FeatureFlag[];
        pagination: PaginationMeta;
      }>(`${API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.LIST}?${params.toString()}`);

      set({
        flags: response.data ?? [],
        pagination: response.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_FLAGS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load feature flags',
        },
      });
    }
  },

  refreshFlags: async () => {
    const { pagination } = get();
    await get().loadFlags(pagination?.page ?? 1);
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadFlags(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadFlags(1);
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createFlag: async (dto: CreateFeatureFlagDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      // API client unwraps the envelope → `response` is the FeatureFlag itself.
      const response = await api.post<FeatureFlag>(
        API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.CREATE,
        dto
      );

      set({ isCreating: false });
      await get().refreshFlags();
      return response.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError: {
          code: 'CREATE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create feature flag',
        },
      });
      return null;
    }
  },

  updateFlag: async (id: string, dto: UpdateFeatureFlagDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.UPDATE(id), dto);

      set({ isUpdating: false });
      await get().refreshFlags();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError: {
          code: 'UPDATE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update feature flag',
        },
      });
      return false;
    }
  },

  toggleFlag: async (id: string, enabled: boolean): Promise<boolean> => {
    set({ isToggling: id, actionError: null });

    // Optimistic update
    set((state) => ({
      flags: state.flags.map((f) => (f.id === id ? { ...f, enabled } : f)),
    }));

    try {
      const api = getApiClient();
      await api.patch(API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.TOGGLE(id), { enabled });

      set({ isToggling: null });
      return true;
    } catch (error) {
      // Revert optimistic update
      set((state) => ({
        flags: state.flags.map((f) => (f.id === id ? { ...f, enabled: !enabled } : f)),
        isToggling: null,
        actionError: {
          code: 'TOGGLE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to toggle feature flag',
        },
      }));
      return false;
    }
  },

  deleteFlag: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.FEATURE_FLAGS.DELETE(id));

      // Remove from local state
      set((state) => ({
        flags: state.flags.filter((f) => f.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError: {
          code: 'DELETE_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete feature flag',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      listError: null,
      actionError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get count of enabled flags */
export const useEnabledFlagsCount = () => {
  return useFeatureFlagsStore((state) => (state.flags ?? []).filter((f) => f.enabled).length);
};

/** Get flags grouped by scope */
export const useFlagsByScope = () => {
  return useFeatureFlagsStore((state) => {
    const byScope: Record<FeatureFlagScope, FeatureFlag[]> = {
      global: [],
      plan: [],
      tenant: [],
    };
    (state.flags ?? []).forEach((f) => {
      byScope[f.scope].push(f);
    });
    return byScope;
  });
};

/** Get count of flags by scope */
export const useFlagCountByScope = (scope: FeatureFlagScope) => {
  return useFeatureFlagsStore((state) => (state.flags ?? []).filter((f) => f.scope === scope).length);
};

/** Get only global flags */
export const useGlobalFlags = () => {
  return useFeatureFlagsStore((state) => (state.flags ?? []).filter((f) => f.scope === 'global'));
};

/** Check if any feature flags data is loading */
export const useFeatureFlagsLoading = () => {
  const isLoadingList = useFeatureFlagsStore((state) => state.isLoadingList);
  const isCreating = useFeatureFlagsStore((state) => state.isCreating);
  const isUpdating = useFeatureFlagsStore((state) => state.isUpdating);
  const isToggling = useFeatureFlagsStore((state) => state.isToggling);
  const isDeleting = useFeatureFlagsStore((state) => state.isDeleting);
  return isLoadingList || isCreating || isUpdating || isToggling !== null || isDeleting;
};

/** Get active filters count */
export const useFeatureFlagFiltersCount = () => {
  return useFeatureFlagsStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.scope) count++;
    if (state.filters.enabled !== undefined) count++;
    return count;
  });
};
